/**
 * HeadingRemoveButtons - a hoverable x button on host-selected headings (shown at the end of the
 * heading line, like the collapse chevron / drag grip family). Clicking it does NOT edit the doc -
 * it calls the HOST's `onRemove(dataKey)` so the host can confirm and delete through its model API
 * (the deal Scope doc: delete a scope item / a whole functional area), then rebuild the doc.
 * Both callbacks are read live through refs like every other RichTextEditor host callback.
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
const removeKey = new PluginKey('headingRemoveButtons');
/** Small x, drawn inline so the DOM widget needs no React/icon dependency. */
const X_SVG = '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">' +
    '<path stroke="currentColor" stroke-width="1.4" stroke-linecap="round" d="M2 2 L8 8 M8 2 L2 8"/></svg>';
function headingDataKey(node) {
    const raw = node.attrs.dataKey;
    return typeof raw === 'string' && raw ? raw : null;
}
function makeRemoveButton(dataKey, onRemove) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'rte-heading-remove';
    el.setAttribute('contenteditable', 'false');
    el.setAttribute('aria-label', 'Remove');
    el.setAttribute('data-rte-tooltip', 'Remove');
    el.innerHTML = X_SVG;
    // Never let the click become a text selection/edit; removal is a host-side model action.
    el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
    });
    el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onRemove(dataKey);
    });
    return el;
}
function buildDecorations(state, enabled, onRemove) {
    if (!enabled || !onRemove)
        return DecorationSet.empty;
    const decos = [];
    state.doc.descendants((node, pos) => {
        if (node.type.name !== 'heading')
            return true;
        const key = headingDataKey(node);
        const level = Number(node.attrs.level ?? 1);
        if (!key || !enabled(key, level))
            return true;
        // Widget at the END of the heading's inline content (side 1 keeps the caret before it).
        decos.push(Decoration.widget(pos + 1 + node.content.size, () => makeRemoveButton(key, onRemove), {
            side: 1,
            key: `hr-${key}`,
        }));
        return true;
    });
    return DecorationSet.create(state.doc, decos);
}
export const HeadingRemoveButtons = Extension.create({
    name: 'headingRemoveButtons',
    addOptions() {
        return { getEnabled: () => null, getOnRemove: () => null };
    },
    addProseMirrorPlugins() {
        const getEnabled = () => this.options.getEnabled();
        const getOnRemove = () => this.options.getOnRemove();
        return [
            new Plugin({
                key: removeKey,
                props: {
                    decorations: (state) => buildDecorations(state, getEnabled(), getOnRemove()),
                },
            }),
        ];
    },
});
//# sourceMappingURL=headingRemoveButtons.js.map